import React, { useEffect } from "react";
import { PostPaywallProps } from "@/types/modules/post/components";
import { BaseButton } from "@/components/ui-kit/Buttons/BaseButton";
import { ButtonType } from "@/types/ui-kit/buttons";
import Image from "next/image";
import { useAppSelector } from "@/core/store/reduxHooks";
import { getUser } from "@/core/store/app/AppSlice";
import { useAuthUrls } from "@/hooks/useAuthUrls";
import { DIGI_SUBS_PRICE_VALUE } from "@/constants/reporter";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import useScrollingRect from "@/hooks/useScrollingRect";

export const PostPaywallUserContent: React.FC<PostPaywallProps> = ({ onUnlockContent, postId }) => {
  const user = useAppSelector(getUser);
  const { getSubscriptionUrl } = useAuthUrls();
  const remainingUnlocks = 3 - (user?.user_meta.unlocked_posts?.length || 0);
  const isCurrentSection = useScrollingRect("postPaywallUserContent", 52);

  useEffect(() => {
    if (isCurrentSection) {
      pushToDataLayer({
        event: "article_interaction",
        interaction_type: "content_consumed",
      });
    }
  }, [isCurrentSection]);

  return (
    <div
      className="relative w-full before:absolute before:bottom-full before:h-[200px] before:w-full before:bg-gradient-to-t before:from-white dark:before:from-dark"
      id="postPaywallUserContent"
    >
      <div className="mx-auto flex max-w-[500px] flex-col items-center justify-center text-center">
        <div className="mb-[24px] flex h-[64px] w-[64px] items-center justify-center">
          <Image src="/assets/images/icons/paywall-icon.svg" width={64} height={64} alt="" />
        </div>

        {remainingUnlocks === 1 && (
          <h3 className="mb-2 font-mercury text-3xl font-semibold dark:text-white">Zbývá poslední článek zdarma</h3>
        )}
        {remainingUnlocks > 1 && (
          <h3 className="mb-2 font-mercury text-3xl font-semibold dark:text-white">
            Zbývají vám {remainingUnlocks} články zdarma
          </h3>
        )}
        {remainingUnlocks === 0 && (
          <h3 className="mb-2 font-mercury text-3xl font-semibold dark:text-white">
            Celý článek si mohou dočíst pouze předplatitelé
          </h3>
        )}
        {remainingUnlocks > 0 ? (
          <>
            <div className="mx-auto mt-[10px] flex w-full max-w-[280px] flex-col">
              <div className="overflow-hidden rounded-full">
                <BaseButton
                  component="button"
                  onClick={onUnlockContent}
                  beforeIconPath="/assets/images/icons/unlock-white.svg"
                  title="Chci číst dál"
                  type={ButtonType.Primary}
                  fullWidth
                />
              </div>
            </div>
            <p className="font-sm my-[20px] font-sans font-light text-dark-70 dark:text-white">
              Nebo získejte přístup k neomezenému obsahu již&nbsp;od&nbsp;{DIGI_SUBS_PRICE_VALUE}
              &nbsp;Kč&nbsp;měsíčně.
            </p>
            <div className="mx-auto flex w-full max-w-[280px] flex-col">
              <div className="overflow-hidden rounded-full">
                <BaseButton
                  href={getSubscriptionUrl()}
                  title="Chci předplatné"
                  type={ButtonType.TransparentStroked}
                  onClick={() =>
                    pushToDataLayer({
                      event: "article_interaction",
                      interaction_type: "click_cta",
                      interaction_option: "Chci předplatné",
                    })
                  }
                  fullWidth
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="font-sm font-sans font-light text-dark-70 dark:text-white">
              Získejte přístup ke kompletnímu obsahu již &nbsp;od&nbsp;{DIGI_SUBS_PRICE_VALUE}
              &nbsp;Kč&nbsp;měsíčně.
            </p>
            <div className="mx-auto mt-[20px] flex w-full max-w-[280px] flex-col">
              <div className="overflow-hidden rounded-full">
                <BaseButton
                  href={getSubscriptionUrl()}
                  title="Předplaťte si Reportér"
                  type={ButtonType.Primary}
                  onClick={() =>
                    pushToDataLayer({
                      event: "article_interaction",
                      interaction_type: "click_cta",
                      interaction_option: "Předplaťte si Reportér",
                    })
                  }
                  fullWidth
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
